import { Alert } from '../components/rn';
import { getExternalCalendarConflicts } from './selectedCalendarEvents';
import { getAcceptedNosubjectCalendarConflicts } from './nosubjectCalendarConflicts';

type Translate = (key: string, params?: Record<string, string | number>) => string;

export async function confirmCalendarConflict(
  token: string,
  userId: number,
  startDate: Date,
  durationMinutes: number,
  lang: string,
  t: Translate,
  options: { excludeMeetupId?: number } = {}
): Promise<boolean> {
  // Kaynakları bağımsız denetle: bir sağlayıcının geçici hatası diğer kaynakta
  // bulunan gerçek çakışmayı gizlememeli; iki hata da işlemi görünmez kilitlemez.
  const [externalResult, nosubjectResult] = await Promise.allSettled([
    getExternalCalendarConflicts(token, userId, startDate, durationMinutes),
    getAcceptedNosubjectCalendarConflicts(token, startDate, durationMinutes),
  ]);
  const externalConflicts = externalResult.status === 'fulfilled' ? externalResult.value.events : [];
  const tentativeEvents = externalResult.status === 'fulfilled'
    ? externalResult.value.tentativeEvents
    : [];
  const nosubjectConflicts = nosubjectResult.status === 'fulfilled'
    ? nosubjectResult.value.filter((event) => event.id !== options.excludeMeetupId)
    : [];
  const checkIncomplete = externalResult.status === 'rejected'
    || (externalResult.status === 'fulfilled' && externalResult.value.incomplete)
    || nosubjectResult.status === 'rejected';
  if (
    externalConflicts.length === 0
    && tentativeEvents.length === 0
    && nosubjectConflicts.length === 0
    && !checkIncomplete
  ) return true;

  const externalEntry = (event: (typeof externalConflicts)[number]) => {
    const title = event.title || t('calendar.untitledExternalEvent');
    if (event.allDay) return { startsAt: event.startDate.getTime(), text: `• ${title} · ${t('calendar.allDay')}` };
    const start = event.startDate.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    const end = event.endDate.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    return { startsAt: event.startDate.getTime(), text: `• ${title} · ${start}–${end}` };
  };
  const entries = externalConflicts.map(externalEntry);
  entries.push(...nosubjectConflicts.map((event) => {
    const label = t(event.kind === 'call' ? 'call.reservationLabel' : 'calendar.meetupLabel');
    const start = event.startDate.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    const end = event.endDate.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    return { startsAt: event.startDate.getTime(), text: `• ${label} · ${event.other_name} · ${start}–${end}` };
  }));
  entries.sort((a, b) => a.startsAt - b.startsAt);
  const tentativeEntries = tentativeEvents.map(externalEntry)
    .sort((a, b) => a.startsAt - b.startsAt);

  const visible = entries.slice(0, 3).map((entry) => entry.text);
  if (entries.length > visible.length) {
    visible.push(t('calendar.conflictMore', { count: entries.length - visible.length }));
  }
  const visibleTentative = tentativeEntries.slice(0, 3).map((entry) => entry.text);
  if (tentativeEntries.length > visibleTentative.length) {
    visibleTentative.push(t('calendar.conflictMore', {
      count: tentativeEntries.length - visibleTentative.length,
    }));
  }

  const hasConflict = entries.length > 0;
  const hasTentative = tentativeEntries.length > 0;
  const title = t(
    hasConflict
      ? 'calendar.conflictTitle'
      : hasTentative
        ? 'calendar.tentativeConflictTitle'
        : 'calendar.checkIncompleteTitle'
  );
  const bodyParts = [
    t(
      hasConflict
        ? 'calendar.conflictBody'
        : hasTentative
          ? 'calendar.tentativeConflictBody'
          : 'calendar.checkIncompleteBody'
    ),
  ];
  if (visible.length > 0) bodyParts.push(visible.join('\n'));
  if (hasConflict && hasTentative) bodyParts.push(t('calendar.tentativeConflictBody'));
  if (visibleTentative.length > 0) bodyParts.push(visibleTentative.join('\n'));
  if ((hasConflict || hasTentative) && checkIncomplete) {
    bodyParts.push(t('calendar.conflictCheckIncomplete'));
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    Alert.alert(
      title,
      bodyParts.join('\n\n'),
      [
        { text: t('common.cancel'), style: 'cancel', onPress: () => finish(false) },
        { text: t('calendar.continueAnyway'), onPress: () => finish(true) },
      ],
      { cancelable: true, onDismiss: () => finish(false) }
    );
  });
}
